/**
 * useServiceOrderSchedules - Histórico e gestão de agendamentos da OS (BR-OS-030)
 *
 * RELEASE:
 *   2026-10-10 v1.0.0 - Versão inicial: listar, agendar/reagendar e cancelar
 *                       agendamento via RPCs protegidas.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export type ServiceOrderScheduleStatus = 'active' | 'rescheduled' | 'cancelled' | 'done';

export interface ServiceOrderSchedule {
  id: string;
  scheduled_start: string;
  scheduled_end: string | null;
  provider_person_id: string | null;
  provider_name: string | null;
  status: ServiceOrderScheduleStatus;
  note: string | null;
  created_at: string;
}

/** Rótulos exibidos na coluna Status */
export const SCHEDULE_STATUS_LABEL: Record<ServiceOrderScheduleStatus, string> = {
  active: 'Confirmado',
  rescheduled: 'Reagendado',
  cancelled: 'Cancelado',
  done: 'Realizado',
};

/** Só o agendamento vigente recebe ações (reagendar/cancelar) */
export const canManageSchedule = (s: Pick<ServiceOrderSchedule, 'status'>, orderStatus: string) =>
  s.status === 'active' && orderStatus === 'scheduled';

// Preserva o receiver do supabase.rpc (regra do projeto)
const rpc = (name: string, args: Record<string, unknown>) => (supabase.rpc as any).call(supabase, name, args);

export function useServiceOrderSchedules(serviceOrderId: string | null) {
  const { currentTenant } = useAuth();
  const hint = currentTenant?.id ?? null;
  return useQuery({
    queryKey: ['service-orders', 'schedules', hint, serviceOrderId],
    enabled: !!hint && !!serviceOrderId,
    queryFn: async () => {
      const { data, error } = await rpc('get_service_order_schedules', { p_service_order_id: serviceOrderId, p_tenant_hint: hint });
      if (error) throw error;
      return (data ?? []) as ServiceOrderSchedule[];
    },
  });
}

/** Invalida OS e calendário após mudança de agenda */
function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['service-orders'] });
    qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0] ?? '').startsWith('calendar') });
  };
}

export function useRescheduleServiceOrder() {
  const { currentTenant } = useAuth();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (i: { id: string; start: string; end?: string | null; providerId?: string | null; note?: string }) => {
      const { data, error } = await rpc('reschedule_service_order', {
        p_id: i.id, p_scheduled_start: i.start, p_scheduled_end: i.end ?? null,
        p_provider_person_id: i.providerId ?? null, p_note: (i.note ?? '').slice(0, 500) || null,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useCancelServiceOrderSchedule() {
  const { currentTenant } = useAuth();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (i: { id: string; note: string }) => {
      const { error } = await rpc('cancel_service_order_schedule', {
        p_id: i.id, p_note: i.note.trim().slice(0, 500), p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}
